import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  agentVersionsApi,
  type Agent,
  type AgentVersion,
} from "@/features/agents/api";
import { cn } from "@/lib/utils";

const KEY = ["agent-versions"] as const;

const STATUS_STYLES: Record<string, string> = {
  published:
    "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  draft:
    "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
  archived:
    "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
};

export default function AgentVersionsTab({
  agent,
  canEdit,
}: {
  agent: Agent;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [...KEY, agent.id],
    queryFn: () => agentVersionsApi.list(agent.id),
    staleTime: 30_000,
  });
  const versions = q.data ?? [];

  const [publishOpen, setPublishOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<AgentVersion | null>(
    null,
  );

  function invalidate() {
    qc.invalidateQueries({ queryKey: [...KEY, agent.id] });
    qc.invalidateQueries({ queryKey: ["agents", "detail", agent.id] });
    qc.invalidateQueries({ queryKey: ["agents", "list"] });
  }

  // Best-effort "current version number" — backend doesn't always return it
  // on Agent; fall back to the highest published version_number.
  const currentVersion =
    (agent as Agent & { version?: number }).version ??
    Math.max(0, ...versions.map((v) => v.version_number));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Version History</h2>
        {canEdit && (
          <Button
            size="sm"
            onClick={() => setPublishOpen(true)}
            disabled={agent.status === "archived"}
          >
            <Send className="size-4" />
            Publish New Version
          </Button>
        )}
      </div>

      {q.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {!q.isLoading && versions.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
            <History className="size-6" />
            <p>
              No versions yet. Click "Publish New Version" to create the first
              snapshot.
            </p>
          </CardContent>
        </Card>
      )}

      {!q.isLoading && versions.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Version</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Published At</th>
                    <th className="px-4 py-3 font-medium">Published By</th>
                    <th className="px-4 py-3 font-medium">Change Summary</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {versions.map((v) => (
                    <tr key={v.id}>
                      <td className="px-4 py-3">
                        <span className="font-semibold">
                          v{v.version_number}
                        </span>
                        {currentVersion === v.version_number && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                            current
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                            STATUS_STYLES[v.status] ??
                              "text-muted-foreground bg-muted",
                          )}
                        >
                          {v.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {v.published_at
                          ? new Date(v.published_at).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {v.published_by || "—"}
                      </td>
                      <td
                        className="max-w-[240px] truncate px-4 py-3"
                        title={v.change_summary || ""}
                      >
                        {v.change_summary || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canEdit && v.status === "archived" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setRollbackTarget(v)}
                          >
                            <Undo2 className="size-3.5" />
                            Rollback
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <PublishDialog
        agent={agent}
        open={publishOpen}
        onOpenChange={setPublishOpen}
        currentVersion={currentVersion}
        onPublished={invalidate}
      />

      <RollbackDialog
        agent={agent}
        target={rollbackTarget}
        onClose={() => setRollbackTarget(null)}
        currentVersion={currentVersion}
        onRolledBack={invalidate}
      />
    </div>
  );
}

function PublishDialog({
  agent,
  open,
  onOpenChange,
  currentVersion,
  onPublished,
}: {
  agent: Agent;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentVersion: number;
  onPublished: () => void;
}) {
  const [summary, setSummary] = useState("");
  const publish = useMutation({
    mutationFn: () => agentVersionsApi.publish(agent.id, summary),
  });

  async function submit() {
    try {
      const res = await publish.mutateAsync();
      toast.success(`Published as version ${res.version}`);
      setSummary("");
      onOpenChange(false);
      onPublished();
    } catch {
      toast.error("Failed to publish agent");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Publish Agent</DialogTitle>
          <DialogDescription>
            This will snapshot the current config as version{" "}
            {currentVersion + 1} and make it live.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="publish-summary">Change Summary (optional)</Label>
          <Textarea
            id="publish-summary"
            rows={3}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="What changed in this version?"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={publish.isPending}>
            {publish.isPending ? "Publishing…" : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RollbackDialog({
  agent,
  target,
  onClose,
  currentVersion,
  onRolledBack,
}: {
  agent: Agent;
  target: AgentVersion | null;
  onClose: () => void;
  currentVersion: number;
  onRolledBack: () => void;
}) {
  const rollback = useMutation({
    mutationFn: () => agentVersionsApi.rollback(agent.id, target!.id),
  });

  async function confirm() {
    if (!target) return;
    try {
      const res = await rollback.mutateAsync();
      toast.success(
        `Rolled back to v${target.version_number} — now v${res.new_version}`,
      );
      onClose();
      onRolledBack();
    } catch {
      toast.error("Failed to rollback agent");
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rollback to v{target?.version_number}?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            This will apply the config from v{target?.version_number} and
            publish it as v{currentVersion + 1}. Current config will be
            archived.
          </div>
          {target?.change_summary && (
            <p className="text-sm">
              <span className="font-medium">Original note:</span>{" "}
              {target.change_summary}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={rollback.isPending}
            className="bg-amber-600 text-white hover:bg-amber-600/90"
          >
            {rollback.isPending ? "Rolling back…" : "Confirm Rollback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
